import { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast from "../../components/CustomComponents/toast/toast.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";
import {
  labelForCancelReasonCode,
  labelForCancelledByType,
} from "../../utils/marketplaceCancelReasonLabels.js";
import "../compnay/ManageCompany.css";
import "./marketplacePerformance.css";

const ALL_COMPANIES = { value: 0, label: "All companies" };

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmtMin = (v) => {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return `${Number(v).toFixed(1)} min`;
};

const pct = (part, whole) => {
  if (!whole || whole <= 0) return 0;
  return Math.round((part / whole) * 1000) / 10;
};

const MarketplacePerformance = () => {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [companyFilter, setCompanyFilter] = useState(ALL_COMPANIES);
  const [companies, setCompanies] = useState([]);
  const [summary, setSummary] = useState(null);

  const companyOptions = useMemo(() => {
    const opts = companies
      .map((c) => {
        const id = c.compID ?? c.CompID;
        if (id == null) return null;
        const name = c.compName || c.CompName || c.name || c.Name || "Company";
        return { value: id, label: `${name} (#${id})` };
      })
      .filter(Boolean);
    return [ALL_COMPANIES, ...opts];
  }, [companies]);

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_ALL_COMPANIES, {
        params: { PageSize: 500, PageNumber: 1, SortBy: "CompName", SortOrder: "ASC" },
      });
      if (!response.data?.status) {
        setCompanies([]);
        return;
      }
      const payload = response.data.data;
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.companies)
          ? payload.companies
          : Array.isArray(payload?.Companies)
            ? payload.Companies
            : [];
      setCompanies(list);
    } catch {
      setCompanies([]);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { FromDate: fromDate, ToDate: toDate };
      const selectedCompId = Number(companyFilter?.value || 0);
      if (selectedCompId > 0) params.CompID = selectedCompId;
      const response = await axiosInstance.get(APIs.GET_MARKETPLACE_PERFORMANCE, { params });
      if (response.data?.status) setSummary(response.data.data);
      else showErrorToast(response.data?.message || "Failed to load performance");
    } catch {
      showErrorToast("Failed to load Go Fatafat performance");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, companyFilter?.value]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const s = summary || {};
  const total = num(s.totalOrders ?? s.TotalOrders);
  const completed = num(s.completedOrders ?? s.CompletedOrders);
  const cancelled = num(s.cancelledOrders ?? s.CancelledOrders);
  const failedReturns = num(s.failedDeliveryReturns ?? s.FailedDeliveryReturns);
  const stuck = num(s.stuckUnassignedOrders ?? s.StuckUnassignedOrders);
  const cancelRate = pct(cancelled, total);
  const completeRate = pct(completed, total);

  const sources = useMemo(
    () => [
      {
        key: "Customer",
        count: num(s.cancelledByCustomer ?? s.CancelledByCustomer),
        color: "#0d9488",
      },
      {
        key: "Restaurant",
        count: num(s.cancelledByRestaurant ?? s.CancelledByRestaurant),
        color: "#b45309",
      },
      {
        key: "Rider",
        count: num(s.cancelledByRider ?? s.CancelledByRider),
        color: "#7c3aed",
      },
      {
        key: "SuperAdmin",
        count: num(s.cancelledBySuperAdmin ?? s.CancelledBySuperAdmin),
        color: "#1d4ed8",
      },
      {
        key: "System",
        count: num(s.cancelledBySystem ?? s.CancelledBySystem),
        color: "#6b7280",
      },
    ],
    [s]
  );

  const attributed = sources.reduce((sum, row) => sum + row.count, 0);
  const unattributed = Math.max(0, cancelled - attributed);

  const breakdown = useMemo(() => {
    const rows = s.cancelBreakdown || s.CancelBreakdown || [];
    const max = rows.reduce(
      (m, row) => Math.max(m, num(row.cancelCount ?? row.CancelCount)),
      0
    );
    return rows.map((row) => {
      const code = row.cancelReasonCode ?? row.CancelReasonCode ?? "";
      const label = labelForCancelReasonCode(code) || code || "—";
      const typeRaw = row.cancelledByType ?? row.CancelledByType;
      const count = num(row.cancelCount ?? row.CancelCount);
      return {
        code,
        label,
        typeLabel: labelForCancelledByType(typeRaw),
        unattributed: !typeRaw || String(typeRaw).toLowerCase() === "unknown",
        count,
        share: pct(count, cancelled || max || 1),
        barWidth: max > 0 ? (count / max) * 100 : 0,
      };
    });
  }, [s, cancelled]);

  const primaryBtn = {
    background: themeColor,
    boxShadow: `0 1px 3px ${themeColor}55`,
  };

  return (
    <div className={`mp-page manage-company-page ${isDarkMode ? "is-dark" : "is-light"}`}>
      {loading && <Preloader />}

      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
        <div>
          <h1 className="page-header mb-1">Go Fatafat Performance</h1>
          <p className="mp-desc">
            Cancel rates, kitchen time, and delivery speed across Go Fatafat orders.
          </p>
        </div>
      </div>

      <div className="mp-toolbar">
        <div className="mp-field">
          <label htmlFor="mp-from">From</label>
          <input
            id="mp-from"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>
        <div className="mp-field">
          <label htmlFor="mp-to">To</label>
          <input
            id="mp-to"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
        <div className="mp-field mp-field-company">
          <label htmlFor="mp-company">Company</label>
          <Select
            inputId="mp-company"
            value={companyFilter}
            onChange={(opt) => setCompanyFilter(opt || ALL_COMPANIES)}
            options={companyOptions}
            styles={dropdownStyles}
            placeholder="All companies"
            isClearable
            isSearchable
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>
        <div className="mp-toolbar-actions">
          <button type="button" className="mp-btn-ghost" onClick={fetchData}>
            Refresh
          </button>
          <button
            type="button"
            className="mp-btn-primary"
            style={primaryBtn}
            onClick={fetchData}
          >
            Apply
          </button>
        </div>
      </div>

      <div className="mp-hero">
        <div className="mp-panel">
          <h2 className="mp-panel-title">Orders</h2>
          <div className="mp-order-grid">
            <div>
              <div className="mp-metric-label">Total</div>
              <div className="mp-metric-value">{total}</div>
            </div>
            <div>
              <div className="mp-metric-label">Completed</div>
              <div className="mp-metric-value is-ok">{completed}</div>
            </div>
            <div>
              <div className="mp-metric-label">Cancelled / Refunded</div>
              <div className="mp-metric-value is-danger">{cancelled}</div>
            </div>
            <div>
              <div className="mp-metric-label">Failed delivery returns</div>
              <div className={`mp-metric-value ${failedReturns > 0 ? "is-danger" : "is-muted"}`}>
                {failedReturns}
              </div>
            </div>
            <div>
              <div className="mp-metric-label">Stuck unassigned</div>
              <div className={`mp-metric-value ${stuck > 0 ? "is-danger" : "is-muted"}`}>
                {stuck}
              </div>
            </div>
          </div>
          <div className="mp-rate-row">
            <span className="mp-rate-label">Cancel rate</span>
            <span className="mp-rate-pct">{cancelRate}%</span>
          </div>
          <div className="mp-bar-track" aria-hidden>
            <div
              className="mp-bar-fill"
              style={{
                width: `${Math.min(100, cancelRate)}%`,
                background: "#b91c1c",
              }}
            />
          </div>
          <div className="mp-rate-row mt-3">
            <span className="mp-rate-label">Completion rate</span>
            <span className="mp-rate-pct">{completeRate}%</span>
          </div>
          <div className="mp-bar-track" aria-hidden>
            <div
              className="mp-bar-fill"
              style={{
                width: `${Math.min(100, completeRate)}%`,
                background: "#047857",
              }}
            />
          </div>
        </div>

        <div className="mp-panel">
          <h2 className="mp-panel-title">Delivery times</h2>
          <div className="mp-timing-list">
            <div className="mp-timing-row">
              <span className="mp-timing-label">Avg kitchen</span>
              <span className="mp-timing-value">
                {fmtMin(s.avgKitchenMinutes ?? s.AvgKitchenMinutes)}
              </span>
            </div>
            <div className="mp-timing-row">
              <span className="mp-timing-label">Assign → deliver</span>
              <span className="mp-timing-value">
                {fmtMin(s.avgAssignToDeliverMinutes ?? s.AvgAssignToDeliverMinutes)}
              </span>
            </div>
            <div className="mp-timing-row">
              <span className="mp-timing-label">Accept → deliver</span>
              <span className="mp-timing-value">
                {fmtMin(s.avgAcceptToDeliverMinutes ?? s.AvgAcceptToDeliverMinutes)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mp-grid-2">
        <div className="mp-panel">
          <h2 className="mp-panel-title">Who cancelled</h2>
          <div className="mp-cancel-sources">
            {sources.map((row) => (
              <div className="mp-source-row" key={row.key}>
                <span className="mp-source-name">{row.key}</span>
                <div className="mp-bar-track" aria-hidden>
                  <div
                    className="mp-bar-fill"
                    style={{
                      width: `${cancelled > 0 ? (row.count / cancelled) * 100 : 0}%`,
                      background: row.color,
                    }}
                  />
                </div>
                <span className="mp-source-count">{row.count}</span>
              </div>
            ))}
            {unattributed > 0 && (
              <div className="mp-source-row">
                <span className="mp-source-name" style={{ color: "#9ca3af" }}>
                  Unattributed
                </span>
                <div className="mp-bar-track" aria-hidden>
                  <div
                    className="mp-bar-fill"
                    style={{
                      width: `${(unattributed / cancelled) * 100}%`,
                      background: "#d1d5db",
                    }}
                  />
                </div>
                <span className="mp-source-count">{unattributed}</span>
              </div>
            )}
          </div>
        </div>

        <div className="mp-panel mp-breakdown-panel">
          <h2 className="mp-panel-title">Cancel breakdown</h2>
          {breakdown.length === 0 ? (
            <div className="mp-empty">No cancels in this date range.</div>
          ) : (
            <div className="table-responsive">
              <table className="mp-table">
                <thead>
                  <tr>
                    <th>Reason</th>
                    <th>By</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {breakdown.map((row, idx) => (
                    <tr key={`${row.code}-${row.typeLabel}-${idx}`}>
                      <td className="mp-reason-cell">
                        <span className="mp-reason-main">{row.label}</span>
                        {row.label !== row.code && row.code ? (
                          <span className="mp-reason-code">{row.code}</span>
                        ) : null}
                      </td>
                      <td>
                        <span
                          className={`mp-type-pill ${
                            row.unattributed ? "is-unattributed" : ""
                          }`}
                        >
                          {row.typeLabel}
                        </span>
                      </td>
                      <td className="mp-count-cell">
                        <div className="mp-count-top">
                          <span className="mp-count-num">{row.count}</span>
                          <span className="mp-count-share">{row.share}%</span>
                        </div>
                        <div className="mp-bar-track" aria-hidden>
                          <div
                            className="mp-bar-fill"
                            style={{
                              width: `${row.barWidth}%`,
                              background: themeColor,
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketplacePerformance;
